import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { getPersistedKeyInitiativeGoalMode } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { normalizeKeyInitiativeStatus } from 'partner360/entities/keyInitiatives/model/status'

import { IListFilterNode, TListFilterDimension, TListFilters } from '../models'

import { LIST_STATUS_ORDER } from './getGroupedInitiativeRows'
import {
  EMPTY_LIST_VALUE,
  getListBusinessPriorities,
  getListInitiativeKey,
  getListStrategicPath,
  splitListValues,
} from './listDictionaries'
import { getListQuarters, LIST_QUARTERS } from './listPeriod'

export const EMPTY_LIST_FILTERS: TListFilters = {
  strategicInitiatives: [],
  businessPriorities: [],
  businessUnits: [],
  periods: [],
  statuses: [],
  goalModes: [],
  fundingSources: [],
  countryCodes: [],
}

export const getListDimensionValues = (
  initiative: TKeyInitiative,
  dimension: TListFilterDimension
): string[] => {
  let values: string[]
  switch (dimension) {
    case 'strategicInitiatives':
      values = initiative.Development?.trim() ? [initiative.Development.trim()] : []
      break
    case 'businessPriorities':
      values = splitListValues(initiative.BusinessPriorities)
      break
    case 'businessUnits':
      values = splitListValues(initiative.BusinessUnit)
      break
    case 'periods':
      values = getListQuarters(initiative.Period)
      break
    case 'statuses':
      values = initiative.Status?.trim() ? [normalizeKeyInitiativeStatus(initiative.Status)] : []
      break
    case 'goalModes':
      values = [getPersistedKeyInitiativeGoalMode(initiative)]
      break
    case 'fundingSources':
      values = initiative.HPInvestmentSource?.trim() ? [initiative.HPInvestmentSource.trim()] : []
      break
    case 'countryCodes':
      values = 'CountryCode' in initiative && initiative.CountryCode ? [initiative.CountryCode] : []
      break
  }
  return values.length ? values : [EMPTY_LIST_VALUE]
}

export const filterInitiativeList = (
  data: TKeyInitiative[],
  filters: TListFilters,
  priorities: IKIBusinessPriority[],
  keyInitiativeIds?: number[]
): TKeyInitiative[] =>
  data.filter((initiative) => {
    // The Quality tab intentionally selects exact initiatives and disables all ordinary filters.
    if (keyInitiativeIds?.length) return keyInitiativeIds.includes(initiative.EntryId)
    if (!initiative.Parent) return false
    return (Object.keys(filters) as TListFilterDimension[]).every((dimension) => {
      const selected = filters[dimension]
      if (!selected.length) return true
      const values = getListDimensionValues(initiative, dimension)
      // Legacy dashboard filters stored names; the new checkboxes keep the actual backend UID.
      const priorityNames =
        dimension === 'businessPriorities'
          ? getListBusinessPriorities(initiative.BusinessPriorities, priorities).map(
              (priority) => priority.name
            )
          : []
      return selected.some(
        (value) =>
          values.some((item) =>
            dimension === 'businessPriorities'
              ? item.toLowerCase() === value.toLowerCase()
              : dimension === 'statuses'
                ? normalizeKeyInitiativeStatus(item) === normalizeKeyInitiativeStatus(value)
                : item === value
          ) || priorityNames.includes(value)
      )
    })
  })

export const toggleListFilterNode = (
  selected: string[],
  values: string[],
  checked: boolean
): string[] =>
  checked
    ? [...new Set([...selected, ...values])]
    : selected.filter((value) => !values.includes(value))

/** Derive facets from all loaded initiatives, never the current filtered/visible subset. */
export const getListFilterOptions = (
  data: TKeyInitiative[],
  objectives: IKeyObjective[],
  priorities: IKIBusinessPriority[],
  translate: (label: string) => string = (label) => label
): Record<TListFilterDimension, IListFilterNode[]> => {
  const dimensions = Object.keys(EMPTY_LIST_FILTERS) as TListFilterDimension[]
  const nodes = Object.fromEntries(
    dimensions.map((dimension) => [
      dimension,
      new Map<string, { node: IListFilterNode; ids: Set<string> }>(),
    ])
  ) as Record<TListFilterDimension, Map<string, { node: IListFilterNode; ids: Set<string> }>>
  const tree: IListFilterNode[] = []
  const treeIds = new Map<string, Set<string>>()
  data.forEach((initiative) => {
    const initiativeKey = getListInitiativeKey(initiative)
    const { path } = getListStrategicPath(initiative, objectives)
    const development = initiative.Development?.trim() || EMPTY_LIST_VALUE
    let level = tree
    ;(path.length ? path : [EMPTY_LIST_VALUE]).forEach((label, index) => {
      const id = JSON.stringify(
        path.slice(0, index + 1).length ? path.slice(0, index + 1) : [EMPTY_LIST_VALUE]
      )
      let node = level.find((candidate) => candidate.id === id)
      if (!node) {
        node = { id, label, values: [], count: 0, children: [] }
        level.push(node)
      }
      node.values = [...new Set([...node.values, development])]
      const ids = treeIds.get(id) ?? new Set<string>()
      ids.add(initiativeKey)
      treeIds.set(id, ids)
      node.count = ids.size
      level = node.children ?? []
    })
    dimensions
      .filter((dimension) => dimension !== 'strategicInitiatives')
      .forEach((dimension) => {
        getListDimensionValues(initiative, dimension).forEach((value) => {
          let label = value
          if (dimension === 'businessPriorities')
            label =
              getListBusinessPriorities(value === EMPTY_LIST_VALUE ? null : value, priorities)[0]
                ?.name ?? value
          if (dimension === 'countryCodes' && 'Country' in initiative && initiative.Country)
            label = initiative.Country
          const id = dimension === 'businessPriorities' ? value.toLowerCase() : value
          const entry = nodes[dimension].get(id) ?? {
            node: { id: value, label, values: [value], count: 0 },
            ids: new Set<string>(),
          }
          entry.ids.add(initiativeKey)
          entry.node.values = [...new Set([...entry.node.values, value])]
          entry.node.count = entry.ids.size
          nodes[dimension].set(id, entry)
        })
      })
  })
  const sortNodes = (items: IListFilterNode[]) => {
    items.sort((left, right) =>
      left.label === EMPTY_LIST_VALUE
        ? 1
        : right.label === EMPTY_LIST_VALUE
          ? -1
          : translate(left.label).localeCompare(translate(right.label))
    )
    items.forEach((node) => node.children && sortNodes(node.children))
  }
  sortNodes(tree)
  const result = Object.fromEntries(
    dimensions.map((dimension) => [
      dimension,
      [...nodes[dimension].values()].map((entry) => entry.node),
    ])
  ) as Record<TListFilterDimension, IListFilterNode[]>
  dimensions.forEach((dimension) => sortNodes(result[dimension]))
  result.strategicInitiatives = tree
  result.statuses.sort((left, right) => {
    const index = (value: string) => {
      const found = LIST_STATUS_ORDER.indexOf(value)
      return found === -1 ? LIST_STATUS_ORDER.length : found
    }
    return index(left.id) - index(right.id)
  })
  const quarterNodes = LIST_QUARTERS.flatMap((quarter) =>
    result.periods.filter((node) => node.id === quarter)
  )
  const periodCount = new Set(
    data.filter((initiative) => getListQuarters(initiative.Period).length).map(getListInitiativeKey)
  ).size
  result.periods = [
    ...(quarterNodes.length
      ? [
          {
            id: 'all-quarters',
            label: 'full-year',
            values: quarterNodes.map((node) => node.id),
            count: periodCount,
            children: quarterNodes,
          },
        ]
      : []),
    ...result.periods.filter((node) => node.id === EMPTY_LIST_VALUE),
  ]
  return result
}
